/**
 * STL in, world-space triangles out.
 *
 * An STL says nothing about which way is up, what a unit means, or where north
 * is - a CAD export is just a bag of triangles in whatever frame the author
 * happened to use. Every one of those has to be supplied by the person who
 * dropped the file in, and getting any of them wrong produces a building that
 * is lying on its side, a thousand times too big, or facing the wrong way. So
 * the transform is explicit, reversible, and reported back with dimensions the
 * user can sanity-check against the real thing.
 *
 * World frame throughout the viewer: x = east, y = north, z = up, metres.
 */

const DEG = Math.PI / 180;

/** Metres per unit, for the units a CAD export actually uses. */
export const UNIT_SCALES = {
  m: 1,
  cm: 0.01,
  mm: 0.001,
  ft: 0.3048,
  in: 0.0254,
};

/**
 * Is this buffer a binary STL?
 *
 * The usual test - does it start with "solid"? - is wrong often enough to
 * matter: plenty of binary exporters write "solid" into the 80-byte header.
 * The reliable test is the size the triangle count implies. If it matches
 * exactly, it is binary, whatever the header says.
 */
function looksBinary(buffer) {
  if (buffer.byteLength < 84) return false;
  const view = new DataView(buffer);
  const count = view.getUint32(80, true);
  return 84 + count * 50 === buffer.byteLength;
}

function parseBinary(buffer) {
  const view = new DataView(buffer);
  const count = view.getUint32(80, true);
  const out = new Float32Array(count * 9);
  let o = 0;
  for (let i = 0; i < count; i += 1) {
    // 50 bytes each: 3 floats of normal (ignored - recomputed when shading, so
    // a file with wrong or zero normals still renders), 9 floats of vertices,
    // 2 bytes of attribute.
    const base = 84 + i * 50 + 12;
    for (let v = 0; v < 9; v += 1) {
      out[o] = view.getFloat32(base + v * 4, true);
      o += 1;
    }
  }
  return { triangles: out, format: "binary" };
}

function parseAscii(text) {
  // Deliberately not a line-by-line state machine: ASCII STL indentation and
  // line endings vary wildly between exporters, and every vertex line has the
  // same shape. Pulling the numbers out directly is both shorter and more
  // tolerant.
  const verts = [];
  const re = /vertex\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)\s+(-?[\d.eE+-]+)/g;
  let m = re.exec(text);
  while (m) {
    verts.push(Number(m[1]), Number(m[2]), Number(m[3]));
    m = re.exec(text);
  }
  if (verts.length === 0 || verts.length % 9 !== 0) {
    throw new Error(
      `This does not look like a readable STL: found ${verts.length / 3} vertices, ` +
        "which is not a whole number of triangles."
    );
  }
  return { triangles: Float32Array.from(verts), format: "ascii" };
}

/** Parse an STL (binary or ASCII) into a flat triangle array, 9 floats each. */
export function parseSTL(buffer) {
  if (!buffer || buffer.byteLength < 15) {
    throw new Error("The file is too small to be an STL.");
  }
  if (looksBinary(buffer)) return parseBinary(buffer);
  const text = new TextDecoder().decode(new Uint8Array(buffer));
  if (!/facet|vertex/i.test(text)) {
    throw new Error(
      "This file is neither a binary STL (its size does not match its triangle " +
        "count) nor an ASCII one (it contains no facets)."
    );
  }
  return parseAscii(text);
}

/** Axis-aligned bounds of a flat triangle array. */
export function boundsOf(triangles) {
  if (!triangles || triangles.length === 0) return null;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < triangles.length; i += 3) {
    for (let k = 0; k < 3; k += 1) {
      const v = triangles[i + k];
      if (v < min[k]) min[k] = v;
      if (v > max[k]) max[k] = v;
    }
  }
  return {
    min,
    max,
    size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]],
    center: [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2],
  };
}

/**
 * Put a parsed mesh into the scene's world frame.
 *
 * Order matters and is fixed: scale, then swap the up axis, then rotate about
 * vertical for north, then translate. Rotating before fixing which axis is up
 * would spin the model about the wrong axis, which is the failure that makes a
 * building appear to lean.
 *
 * `dropToGround` sets the model's lowest point onto the ground beneath it
 * (plus `baseHeight`). Almost every CAD export is drawn from an arbitrary
 * origin, and a building floating three metres up casts a shadow that starts in
 * the wrong place.
 *
 * On sloped terrain "the ground" is not z = 0, which is why `groundZAt` exists.
 * Pinning the model to the horizontal datum instead put a building 25 m down a
 * 10 deg slope some 4.4 m into the air, and its shadow started from there. The
 * height is taken under the model's final CENTRE - a single number, so the model
 * stays rigid and level rather than being draped over the terrain. A building
 * does sit level; a long wall running across a slope does not, and this will
 * put one end of it slightly into the ground or above it.
 *
 * @returns {{triangles: Float32Array, bounds: object}}
 */
export function placeMesh(
  triangles,
  {
    upAxis = "z",
    units = "m",
    northRotationDeg = 0,
    east = 0,
    north = 0,
    baseHeight = 0,
    dropToGround = true,
    groundZAt = null,
  } = {}
) {
  const scale = UNIT_SCALES[units] ?? 1;
  const out = new Float32Array(triangles.length);

  // Step 1 and 2: scale and orient. Y-up (the graphics convention) becomes
  // Z-up (the CAD and surveying one) by (x, y, z) -> (x, -z, y): a right-handed
  // swap, so the model is not mirrored - a mirrored building is a subtle bug,
  // because it looks perfectly plausible until its shadow falls the wrong side.
  for (let i = 0; i < triangles.length; i += 3) {
    const x = triangles[i] * scale;
    const y = triangles[i + 1] * scale;
    const z = triangles[i + 2] * scale;
    if (upAxis === "y") {
      out[i] = x;
      out[i + 1] = -z;
      out[i + 2] = y;
    } else {
      out[i] = x;
      out[i + 1] = y;
      out[i + 2] = z;
    }
  }

  // Step 3: rotate about vertical so the model's north matches the site's.
  // Clockwise seen from above, matching every other azimuth in the application.
  const a = northRotationDeg * DEG;
  if (a !== 0) {
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    for (let i = 0; i < out.length; i += 3) {
      const x = out[i];
      const y = out[i + 1];
      out[i] = x * ca + y * sa;
      out[i + 1] = -x * sa + y * ca;
    }
  }

  // Step 4: translate. Centred horizontally on the array, then offset, so
  // "0, 0" means "on top of the rows" rather than "wherever the CAD origin was".
  const b = boundsOf(out);
  const dx = east - b.center[0];
  const dy = north - b.center[1];
  // The model's centre after translation is exactly (east, north), so the
  // terrain height under it is known before the points move.
  const groundHere =
    dropToGround && typeof groundZAt === "function" ? groundZAt(east, north) : 0;
  const dz = dropToGround ? baseHeight + groundHere - b.min[2] : baseHeight;
  for (let i = 0; i < out.length; i += 3) {
    out[i] += dx;
    out[i + 1] += dy;
    out[i + 2] += dz;
  }

  return { triangles: out, bounds: boundsOf(out) };
}

/**
 * A short, checkable description of what was loaded.
 *
 * Footprint and height are the two numbers a user can compare against the real
 * building without opening anything, which makes them the fastest way to catch
 * a units mistake - a 25 m block that reads as 25 mm or 25000 m is obvious.
 */
export function describeMesh(triangles, bounds) {
  if (!bounds) return "";
  const [w, d, h] = bounds.size;
  return (
    `${(triangles.length / 9).toLocaleString("en-GB")} triangles · footprint ` +
    `${w.toFixed(1)} × ${d.toFixed(1)} m · height ${h.toFixed(1)} m`
  );
}
